import React, { useEffect, useMemo, useState } from "react";
import { Loading } from "../components/Loader";
import { Select } from "../components/Select";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import PaginationBar from "../components/PaginationBar";
import { inventoryRevisionStatusLabel } from "../shared/inventoryRevisionEnums";
import {
  fetchInventoryRevisions,
  selectInventoryRevisions,
  selectInventoryRevisionsError,
  selectInventoryRevisionsStatus,
} from "../features/inventoryRevisions/inventoryRevisionsSlice";
import "./WarehouseRevisionPage.css";

function formatDateTime(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toLocaleString("ru-RU");
}

function statusColor(status) {
  const s = String(status || "").toLowerCase();
  if (s === "in_progress") return "var(--c-accent)";
  if (s === "draft") return "var(--c-text-sub)";
  if (s === "counted" || s === "submitted") return "var(--c-accent)";
  if (s === "approved" || s === "posted") return "var(--c-success)";
  if (s === "cancelled") return "var(--c-danger)";
  return "var(--c-text-sub)";
}

export default function WarehouseRevisionPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useDispatch();

  const PAGE_SIZE = 25;

  const [search, setSearch] = useState(() => searchParams.get("search") || "");
  // Default filter: exclude posted/cancelled but allow switching to "All statuses"
  const [status, setStatus] = useState(() => searchParams.get("status") ?? "active");
  const [refreshKey, setRefreshKey] = useState(0);
  const [page, setPage] = useState(() => {
    const raw = searchParams.get("page");
    const n = Number(raw || 1);
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : 1;
  });

  const rows = useSelector(selectInventoryRevisions);
  const loadStatus = useSelector(selectInventoryRevisionsStatus);
  const error = useSelector(selectInventoryRevisionsError);

  const searchTrimmed = useMemo(() => search.trim(), [search]);
  const statusTrimmed = useMemo(() => status.trim(), [status]);

  // Keep URL in sync, so Back/Reload preserves pagination/filters
  useEffect(() => {
    const next = new URLSearchParams();
    if (searchTrimmed) next.set("search", searchTrimmed);
    // default is "active"
    if (String(statusTrimmed) !== "active") next.set("status", String(statusTrimmed));
    if (page && page !== 1) next.set("page", String(page));
    setSearchParams(next, { replace: true });
  }, [page, searchTrimmed, setSearchParams, statusTrimmed]);

  // Hydrate local state from URL (Back/Forward)
  useEffect(() => {
    const urlSearch = searchParams.get("search") || "";
    const urlStatus = searchParams.get("status") ?? "active";
    const urlPageRaw = searchParams.get("page");
    const urlPageNum = Number(urlPageRaw || 1);
    const urlPage = Number.isFinite(urlPageNum) && urlPageNum > 0 ? Math.floor(urlPageNum) : 1;

    if (urlSearch !== search) setSearch(urlSearch);
    if (urlStatus !== status) setStatus(urlStatus);
    if (urlPage !== page) setPage(urlPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const filteredRows = useMemo(() => {
    const s = String(statusTrimmed || "");
    if (s === "active") {
      return (rows || []).filter((r) => {
        const st = String(r?.status || "").toLowerCase();
        return st !== "posted" && st !== "cancelled";
      });
    }
    return rows || [];
  }, [rows, statusTrimmed]);

  const totalCount = Array.isArray(filteredRows) ? filteredRows.length : 0;
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  useEffect(() => {
    // Clamp only after data is loaded to avoid resetting page on hard reload.
    if (loadStatus === "succeeded" && page > totalPages) setPage(totalPages);
  }, [loadStatus, page, totalPages]);

  const visibleRows = useMemo(() => {
    const list = Array.isArray(filteredRows) ? filteredRows : [];
    const start = (page - 1) * PAGE_SIZE;
    return list.slice(start, start + PAGE_SIZE);
  }, [filteredRows, page]);

  useEffect(() => {
    // "active" is a UI-only filter; backend doesn't support it directly.
    dispatch(fetchInventoryRevisions({ search: searchTrimmed, status: statusTrimmed === "active" ? "" : statusTrimmed }));
  }, [dispatch, refreshKey, searchTrimmed, statusTrimmed]);

  return (
    <div className="warehouse-revision-page">
      <div className="header">
        <div>
          <div className="title">Ревизии</div>
          <div className="sub-title">Список ревизий склада (данные из /api/product/inventory-revisions/).</div>
        </div>

        <div className="header-actions">
          <input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Поиск: магазин / id / комментарий / товар…"
            className="search"
          />
          <Select
            value={status}
            onChange={(v) => { setStatus(v);
              setPage(1); }}
            options={[
              { value: "active", label: "Активные (без posted/cancelled)" },
              { value: "", label: "Все статусы" },
              { value: "draft", label: inventoryRevisionStatusLabel("draft") },
              { value: "in_progress", label: inventoryRevisionStatusLabel("in_progress") },
              { value: "counted", label: inventoryRevisionStatusLabel("counted") },
              { value: "submitted", label: inventoryRevisionStatusLabel("submitted") },
              { value: "approved", label: inventoryRevisionStatusLabel("approved") },
              { value: "posted", label: inventoryRevisionStatusLabel("posted") },
              { value: "cancelled", label: inventoryRevisionStatusLabel("cancelled") },
            ]}
          />
          <button
            type="button"
            onClick={() => navigate("/warehouse/revision/reports")}
            className="btn"
          >
            📊 Отчёт
          </button>
          <button
            type="button"
            onClick={() =>
              navigate("/warehouse/revision/new", {
                state: { from: `${location.pathname}${location.search}` },
              })
            }
            className="btn-primary"
          >
            + Добавить ревизию
          </button>
          <button type="button" onClick={() => setRefreshKey((v) => v + 1)} className="btn">
            Обновить
          </button>
        </div>
      </div>

      {loadStatus === "loading" && <Loading text="Загрузка…" />}
      {loadStatus === "failed" && <div className="error">{error}</div>}

      <div className="body">
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th className="th">ID</th>
                <th className="th">Магазин</th>
                <th className="th">Статус</th>
                <th className="th">План. завершение</th>
                <th className="th">Просрочено</th>
                <th className="th">Позиций</th>
                <th className="th">Создано</th>
                <th className="th">Обновлено</th>
              </tr>
            </thead>
            <tbody>
              {visibleRows.length === 0 && loadStatus === "succeeded" ? (
                <tr>
                  <td colSpan={8} className="td-muted">
                    Ничего не найдено
                  </td>
                </tr>
              ) : (
                visibleRows.map((r) => (
                  <tr
                    key={r.id}
                    className="tr"
                    onClick={() =>
                      navigate(`/warehouse/revision/${r.id}`, {
                        state: { from: `${location.pathname}${location.search}` },
                      })
                    }
                    title="Открыть ревизию"
                  >
                    <td className="td-mono">{r.id}</td>
                    <td className="td">{r.store_name || `Store #${r.store}` || "—"}</td>
                    <td className="td">
                      <span className="badge" style={{ borderColor: statusColor(r.status), color: statusColor(r.status) }}>
                        {inventoryRevisionStatusLabel(r.status)}
                      </span>
                    </td>
                    <td className="td">{formatDateTime(r.planned_finish_at)}</td>
                    <td className="td">
                      {r.is_overdue ? <span className="overdue">Да</span> : <span className="ok">Нет</span>}
                    </td>
                    <td className="td">{typeof r.items_count === "number" ? r.items_count : "—"}</td>
                    <td className="td">{formatDateTime(r.created_at)}</td>
                    <td className="td">{formatDateTime(r.updated_at)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalCount > 0 && (
          <PaginationBar
            page={page}
            totalCount={totalCount}
            pageSize={PAGE_SIZE}
            itemsOnPage={visibleRows.length}
            onPageChange={setPage}
          />
        )}
      </div>
    </div>
  );
}

